import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import { BarChart3, Users, Calendar, TrendingUp, Baby, Sparkles, MapPin, Monitor } from "lucide-react";
import { format, startOfYear, endOfYear, eachMonthOfInterval, getMonth, getYear, differenceInYears, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { AISuggestionsPanel } from "./AISuggestionsPanel";

interface AppointmentData {
  appointment_date: string;
  birth_date: string | null;
  gender: string | null;
  is_online: boolean | null;
  location_id: string | null;
}

interface LocationData {
  id: string;
  name: string;
}

interface QuestionnaireData {
  appointment_id: string;
  responses: Record<string, any>;
}

interface AdditionalStats {
  giftCardsSold: number;
  productsSold: number;
  newsletterSubscribers: number;
}

const DISCOVERY_COLORS = {
  recommandation: "hsl(var(--primary))",
  reseaux_sociaux: "hsl(var(--secondary))",
  internet: "hsl(45, 93%, 47%)",
  autre: "hsl(var(--muted-foreground))",
};

const DISCOVERY_LABELS: Record<string, string> = {
  recommandation: "Recommandation",
  reseaux_sociaux: "Réseaux sociaux",
  internet: "Internet / site web",
  autre: "Autre",
};

const GENDER_COLORS = {
  femme: "hsl(340, 70%, 55%)",
  homme: "hsl(210, 70%, 55%)",
  intersexe: "hsl(150, 70%, 50%)",
  prefere_ne_pas_repondre: "hsl(var(--muted-foreground))",
  non_renseigne: "hsl(0, 0%, 70%)",
};

const GENDER_LABELS: Record<string, string> = {
  femme: "Femme",
  homme: "Homme",
  intersexe: "Intersexe",
  prefere_ne_pas_repondre: "Préfère ne pas répondre",
  non_renseigne: "Non renseigné",
};

// Detailed age ranges including children categories
const AGE_RANGES = [
  { key: "0-3", label: "0-3 ans (Nourrisson)", min: 0, max: 3, category: "enfant" },
  { key: "3-6", label: "3-6 ans (Petite enfance)", min: 3, max: 6, category: "enfant" },
  { key: "6-12", label: "6-12 ans (Enfant)", min: 6, max: 12, category: "enfant" },
  { key: "12-18", label: "12-18 ans (Adolescent)", min: 12, max: 18, category: "enfant" },
  { key: "18-25", label: "18-25 ans", min: 18, max: 25, category: "adulte" },
  { key: "26-35", label: "26-35 ans", min: 26, max: 35, category: "adulte" },
  { key: "36-45", label: "36-45 ans", min: 36, max: 45, category: "adulte" },
  { key: "46-55", label: "46-55 ans", min: 46, max: 55, category: "adulte" },
  { key: "56-65", label: "56-65 ans", min: 56, max: 65, category: "adulte" },
  { key: "66+", label: "66+ ans (Senior)", min: 66, max: 150, category: "adulte" },
  { key: "non_renseigne", label: "Non renseigné", min: -1, max: -1, category: "autre" },
];

const AGE_COLORS = [
  "hsl(280, 70%, 60%)", // 0-3 violet
  "hsl(300, 70%, 55%)", // 3-6 magenta
  "hsl(320, 70%, 55%)", // 6-12 rose
  "hsl(340, 70%, 55%)", // 12-18 rouge-rose
  "hsl(200, 70%, 50%)", // 18-25 bleu
  "hsl(180, 70%, 50%)", // 26-35 cyan
  "hsl(160, 70%, 50%)", // 36-45 turquoise
  "hsl(140, 70%, 50%)", // 46-55 vert
  "hsl(120, 70%, 50%)", // 56-65 vert clair
  "hsl(100, 70%, 50%)", // 66+ vert-jaune
  "hsl(0, 0%, 70%)",    // non renseigné
];

const MONTH_NAMES = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

export function DiscoveryStatistics() {
  const [appointments, setAppointments] = useState<AppointmentData[]>([]);
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireData[]>([]);
  const [additionalStats, setAdditionalStats] = useState<AdditionalStats>({
    giftCardsSold: 0,
    productsSold: 0,
    newsletterSubscribers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch appointments with birth_date, gender, is_online, location_id
      const { data: appointmentsData, error: aptError } = await (supabase as any)
        .from("appointments")
        .select("appointment_date, birth_date, gender, is_online, location_id")
        .not("appointment_date", "is", null);

      if (aptError) throw aptError;

      // Fetch all active locations
      const { data: locationsData, error: locError } = await (supabase as any)
        .from("locations")
        .select("id, name")
        .eq("is_active", true)
        .order("is_default", { ascending: false })
        .order("name", { ascending: true });

      if (locError) throw locError;

      // Fetch questionnaires with responses
      const { data: questionnairesData, error: qError } = await supabase
        .from("welcome_questionnaires")
        .select("appointment_id, responses");

      if (qError) throw qError;

      // Fetch additional stats with type casting to avoid TS deep instantiation
      const giftCardsResult = await (supabase as any)
        .from("gift_cards")
        .select("id", { count: "exact", head: true })
        .eq("status", "paid");

      const ordersResult = await (supabase as any)
        .from("product_orders")
        .select("id", { count: "exact", head: true })
        .eq("status", "paid");

      const newsletterResult = await (supabase as any)
        .from("newsletter_subscribers")
        .select("id", { count: "exact", head: true })
        .eq("confirmed", true);

      setAdditionalStats({
        giftCardsSold: (giftCardsResult.count as number) || 0,
        productsSold: (ordersResult.count as number) || 0,
        newsletterSubscribers: (newsletterResult.count as number) || 0,
      });

      // Set locations
      setLocations((locationsData as LocationData[]) || []);

      // Cast to proper types
      const typedAppointments: AppointmentData[] = ((appointmentsData as any[]) || []).map((apt: any) => ({
        appointment_date: apt.appointment_date as string,
        birth_date: apt.birth_date as string | null,
        gender: apt.gender as string | null,
        is_online: apt.is_online as boolean | null,
        location_id: apt.location_id as string | null,
      }));

      const typedQuestionnaires: QuestionnaireData[] = (questionnairesData || []).map(q => ({
        appointment_id: q.appointment_id,
        responses: (q.responses || {}) as Record<string, any>,
      }));

      setAppointments(typedAppointments);
      setQuestionnaires(typedQuestionnaires);

      // Calculate available years
      const years = new Set<number>();
      typedAppointments.forEach(apt => {
        if (apt.appointment_date) {
          years.add(getYear(parseISO(apt.appointment_date)));
        }
      });
      const sortedYears = Array.from(years).sort((a, b) => b - a);
      setAvailableYears(sortedYears.length > 0 ? sortedYears : [new Date().getFullYear()]);
      if (sortedYears.length > 0 && !sortedYears.includes(selectedYear)) {
        setSelectedYear(sortedYears[0]);
      }
    } catch (error) {
      console.error("Error loading statistics data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter appointments by selected year
  const yearAppointments = appointments.filter(apt => {
    if (!apt.appointment_date) return false;
    return getYear(parseISO(apt.appointment_date)) === selectedYear;
  });

  // Discovery source statistics
  const getDiscoveryStats = () => {
    const counts: Record<string, number> = {
      recommandation: 0,
      reseaux_sociaux: 0,
      internet: 0,
      autre: 0,
    };

    questionnaires.forEach(q => {
      const apt = appointments.find(a => {
        // Match by checking if appointment_id exists
        return true; // We'll count all questionnaires for now
      });
      
      const responses = q.responses as Record<string, any>;
      const commentConnu = responses?.comment_connu;

      if (commentConnu && Array.isArray(commentConnu)) {
        commentConnu.forEach((source: string) => {
          if (source in counts) {
            counts[source]++;
          }
        });
      }
    });

    return Object.entries(counts)
      .map(([key, value]) => ({
        name: DISCOVERY_LABELS[key],
        value,
        key,
      }))
      .filter(d => d.value > 0);
  };

  // Gender statistics
  const getGenderStats = () => {
    const counts: Record<string, number> = {
      femme: 0,
      homme: 0,
      intersexe: 0,
      prefere_ne_pas_repondre: 0,
      non_renseigne: 0,
    };

    yearAppointments.forEach(apt => {
      const gender = apt.gender || "non_renseigne";
      if (gender in counts) {
        counts[gender]++;
      } else {
        counts.non_renseigne++;
      }
    });

    return Object.entries(counts)
      .map(([key, value]) => ({
        name: GENDER_LABELS[key],
        value,
        key,
      }))
      .filter(d => d.value > 0);
  };

  // Age range statistics
  const getAgeStats = () => {
    const counts: Record<string, number> = {};
    AGE_RANGES.forEach(range => {
      counts[range.key] = 0;
    });

    yearAppointments.forEach(apt => {
      if (!apt.birth_date) {
        counts["non_renseigne"]++;
        return;
      }

      try {
        const age = differenceInYears(new Date(), parseISO(apt.birth_date));
        const range = AGE_RANGES.find(r => age >= r.min && age <= r.max);
        if (range) {
          counts[range.key]++;
        }
      } catch {
        counts["non_renseigne"]++;
      }
    });

    return AGE_RANGES.map((range, index) => ({
      name: range.label,
      value: counts[range.key],
      key: range.key,
      color: AGE_COLORS[index],
    })).filter(d => d.value > 0);
  };

  // Monthly statistics
  const getMonthlyStats = () => {
    const monthlyCounts = Array(12).fill(0);

    yearAppointments.forEach(apt => {
      if (apt.appointment_date) {
        const month = getMonth(parseISO(apt.appointment_date));
        monthlyCounts[month]++;
      }
    });

    return MONTH_NAMES.map((name, index) => ({
      name,
      appointments: monthlyCounts[index],
    }));
  };

  // Yearly statistics (all years)
  const getYearlyStats = () => {
    const yearlyCounts: Record<number, number> = {};

    appointments.forEach(apt => {
      if (apt.appointment_date) {
        const year = getYear(parseISO(apt.appointment_date));
        yearlyCounts[year] = (yearlyCounts[year] || 0) + 1;
      }
    });

    return Object.entries(yearlyCounts)
      .map(([year, count]) => ({
        name: year,
        appointments: count,
      }))
      .sort((a, b) => parseInt(a.name) - parseInt(b.name));
  };

  // Location statistics (dynamically based on configured locations)
  const getLocationStats = () => {
    const locationCounts: Record<string, number> = {};
    let visioCount = 0;

    // Initialize counts for all locations
    locations.forEach(loc => {
      locationCounts[loc.id] = 0;
    });

    yearAppointments.forEach(apt => {
      if (apt.is_online === true) {
        visioCount++;
      } else if (apt.location_id && locationCounts[apt.location_id] !== undefined) {
        locationCounts[apt.location_id]++;
      }
    });

    const stats = locations.map(loc => ({
      name: loc.name,
      value: locationCounts[loc.id],
      key: loc.id,
      isVisio: false,
    })).filter(s => s.value > 0);

    // Add visio stats
    if (visioCount > 0) {
      stats.push({
        name: "Visio",
        value: visioCount,
        key: "visio",
        isVisio: true,
      });
    }

    return stats;
  };

  const discoveryData = getDiscoveryStats();
  const genderData = getGenderStats();
  const ageData = getAgeStats();
  const monthlyData = getMonthlyStats();
  const yearlyData = getYearlyStats();
  const locationData = getLocationStats();

  const totalAppointments = yearAppointments.length;
  const totalAllTime = appointments.length;

  // Calculate children vs adults count
  const childrenCount = ageData
    .filter(a => ["0-3", "3-6", "6-12", "12-18"].includes(a.key))
    .reduce((sum, a) => sum + a.value, 0);
  const adultsCount = ageData
    .filter(a => !["0-3", "3-6", "6-12", "12-18", "non_renseigne"].includes(a.key))
    .reduce((sum, a) => sum + a.value, 0);

  // Calculate visio vs cabinet totals
  const visioTotal = locationData.find(l => l.isVisio)?.value || 0;
  const cabinetTotal = locationData.filter(l => !l.isVisio).reduce((sum, l) => sum + l.value, 0);

  // Prepare statistics for AI suggestions
  const statisticsForAI = {
    totalAppointments,
    totalAllTime,
    genderStats: genderData.map(g => ({
      name: g.name,
      value: g.value,
      percentage: totalAppointments > 0 ? ((g.value / totalAppointments) * 100).toFixed(1) : "0",
    })),
    ageStats: ageData.map(a => ({
      name: a.name,
      value: a.value,
      percentage: totalAppointments > 0 ? ((a.value / totalAppointments) * 100).toFixed(1) : "0",
    })),
    discoveryStats: discoveryData.map(d => ({
      name: d.name,
      value: d.value,
    })),
    locationStats: locationData.map(l => ({
      name: l.name,
      value: l.value,
      isVisio: l.isVisio,
      percentage: totalAppointments > 0 ? ((l.value / totalAppointments) * 100).toFixed(1) : "0",
    })),
    visioTotal,
    cabinetTotal,
    monthlyStats: monthlyData,
    selectedYear,
    giftCardsSold: additionalStats.giftCardsSold,
    productsSold: additionalStats.productsSold,
    newsletterSubscribers: additionalStats.newsletterSubscribers,
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="text-muted-foreground">Chargement des statistiques...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Year selector - mobile responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          <span className="text-xs sm:text-sm text-muted-foreground">Année :</span>
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-24 sm:w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs sm:text-sm text-muted-foreground">
          <span className="font-medium">{totalAppointments}</span> RDV en {selectedYear} | <span className="font-medium">{totalAllTime}</span> total
        </div>
      </div>

      {/* Summary cards - mobile responsive grid */}
      <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-4">
        <Card>
          <CardContent className="pt-3 sm:pt-4 px-2 sm:px-4">
            <div className="text-lg sm:text-2xl font-bold">{totalAppointments}</div>
            <p className="text-[10px] sm:text-xs text-muted-foreground">RDV {selectedYear}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 sm:pt-4 px-2 sm:px-4">
            <div className="text-lg sm:text-2xl font-bold">{genderData.find(g => g.key === "femme")?.value || 0}</div>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Femmes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 sm:pt-4 px-2 sm:px-4">
            <div className="text-lg sm:text-2xl font-bold">{genderData.find(g => g.key === "homme")?.value || 0}</div>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Hommes</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="pt-3 sm:pt-4 px-2 sm:px-4">
            <div className="text-lg sm:text-2xl font-bold text-purple-600 flex items-center gap-1">
              <Baby className="w-4 h-4 sm:w-5 sm:h-5" />
              {childrenCount}
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Enfants</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 sm:pt-4 px-2 sm:px-4">
            <div className="text-lg sm:text-2xl font-bold">{adultsCount}</div>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Adultes</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-3 sm:pt-4 px-2 sm:px-4">
            <div className="text-lg sm:text-2xl font-bold text-blue-600 flex items-center gap-1">
              <Monitor className="w-4 h-4 sm:w-5 sm:h-5" />
              {visioTotal}
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Visio</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-3 sm:pt-4 px-2 sm:px-4">
            <div className="text-lg sm:text-2xl font-bold text-green-600 flex items-center gap-1">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
              {cabinetTotal}
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Cabinet</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="timeline" className="space-y-4">
        {/* Mobile: horizontal scroll tabs */}
        <div className="overflow-x-auto -mx-4 px-4 pb-2 sm:mx-0 sm:px-0">
          <TabsList className="inline-flex w-max sm:grid sm:w-full sm:grid-cols-6 h-auto p-1 gap-1">
            <TabsTrigger value="timeline" className="text-xs sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap flex-shrink-0">
              Chronologie
            </TabsTrigger>
            <TabsTrigger value="demographics" className="text-xs sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap flex-shrink-0">
              Démographie
            </TabsTrigger>
            <TabsTrigger value="locations" className="text-xs sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap flex-shrink-0">
              Lieux
            </TabsTrigger>
            <TabsTrigger value="discovery" className="text-xs sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap flex-shrink-0">
              Découverte
            </TabsTrigger>
            <TabsTrigger value="age" className="text-xs sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap flex-shrink-0">
              Tranches d'âge
            </TabsTrigger>
            <TabsTrigger value="suggestions" className="text-xs sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap flex-shrink-0 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              <span className="hidden xs:inline">Suggestions</span> IA
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Monthly Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="w-4 h-4" />
                  Rendez-vous par mois ({selectedYear})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="name" className="text-xs" />
                      <YAxis allowDecimals={false} className="text-xs" />
                      <Tooltip
                        contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }}
                        formatter={(value: number) => [`${value} RDV`, "Rendez-vous"]}
                      />
                      <Bar dataKey="appointments" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Yearly Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="w-4 h-4" />
                  Évolution annuelle
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={yearlyData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="name" className="text-xs" />
                      <YAxis allowDecimals={false} className="text-xs" />
                      <Tooltip
                        contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }}
                        formatter={(value: number) => [`${value} RDV`, "Rendez-vous"]}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="appointments" 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        dot={{ fill: "hsl(var(--primary))", strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Demographics Tab (Gender) */}
        <TabsContent value="demographics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Répartition par genre biologique ({selectedYear})
              </CardTitle>
              <CardDescription>
                Distribution des consultants selon leur genre déclaré
              </CardDescription>
            </CardHeader>
            <CardContent>
              {genderData.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground text-sm">Aucune donnée disponible pour {selectedYear}</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={genderData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {genderData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={GENDER_COLORS[entry.key as keyof typeof GENDER_COLORS] || GENDER_COLORS.non_renseigne} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => [`${value} consultant${value > 1 ? "s" : ""}`, ""]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {genderData.map((item) => {
                      const percentage = totalAppointments > 0 ? ((item.value / totalAppointments) * 100).toFixed(1) : "0";
                      return (
                        <div key={item.key} className="p-3 rounded-lg border bg-card text-center">
                          <div
                            className="w-3 h-3 rounded-full mx-auto mb-2"
                            style={{ backgroundColor: GENDER_COLORS[item.key as keyof typeof GENDER_COLORS] || GENDER_COLORS.non_renseigne }}
                          />
                          <div className="text-xl font-bold">{item.value}</div>
                          <div className="text-xs text-muted-foreground">{item.name}</div>
                          <div className="text-xs text-muted-foreground">({percentage}%)</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Répartition par lieu ({selectedYear})
              </CardTitle>
              <CardDescription>
                Distribution des consultations par lieu et type (visio/cabinet)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {locationData.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground text-sm">Aucune donnée disponible pour {selectedYear}</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={locationData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {locationData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.isVisio ? "hsl(210, 70%, 55%)" : `hsl(${140 + index * 30}, 60%, 50%)`} 
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => [`${value} RDV`, ""]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-3">
                    {locationData.map((item, index) => {
                      const percentage = totalAppointments > 0 ? ((item.value / totalAppointments) * 100).toFixed(1) : "0";
                      return (
                        <div key={item.key} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: item.isVisio ? "hsl(210, 70%, 55%)" : `hsl(${140 + index * 30}, 60%, 50%)` }}
                            />
                            <div className="flex items-center gap-2">
                              {item.isVisio ? (
                                <Monitor className="w-4 h-4 text-blue-500" />
                              ) : (
                                <MapPin className="w-4 h-4 text-green-600" />
                              )}
                              <span className="font-medium">{item.name}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold">{item.value}</div>
                            <div className="text-xs text-muted-foreground">{percentage}%</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Discovery Tab */}
        <TabsContent value="discovery" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Comment m'avez-vous connue ?
              </CardTitle>
              <CardDescription>
                Réponses des questionnaires de bienvenue ({questionnaires.length} questionnaire{questionnaires.length > 1 ? "s" : ""})
              </CardDescription>
            </CardHeader>
            <CardContent>
              {discoveryData.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground text-sm">Aucune donnée disponible</p>
                  <p className="text-muted-foreground text-xs mt-1">
                    Les statistiques seront calculées à partir des questionnaires remplis.
                  </p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={discoveryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {discoveryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={DISCOVERY_COLORS[entry.key as keyof typeof DISCOVERY_COLORS]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => [`${value} réponse${value > 1 ? "s" : ""}`, ""]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(DISCOVERY_LABELS).map(([key, label]) => {
                      const item = discoveryData.find(d => d.key === key);
                      const value = item?.value || 0;
                      const total = discoveryData.reduce((sum, d) => sum + d.value, 0);
                      const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : "0";
                      return (
                        <div key={key} className="p-3 rounded-lg border bg-card text-center">
                          <div
                            className="w-3 h-3 rounded-full mx-auto mb-2"
                            style={{ backgroundColor: DISCOVERY_COLORS[key as keyof typeof DISCOVERY_COLORS] }}
                          />
                          <div className="text-xl font-bold">{value}</div>
                          <div className="text-xs text-muted-foreground">{label}</div>
                          <div className="text-xs text-muted-foreground">({percentage}%)</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Age Tab */}
        <TabsContent value="age" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Tranches d'âge ({selectedYear})
              </CardTitle>
              <CardDescription>
                Répartition des consultants par tranche d'âge (basée sur la date de naissance)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {ageData.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground text-sm">Aucune donnée disponible pour {selectedYear}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ageData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis type="number" allowDecimals={false} className="text-xs" />
                        <YAxis dataKey="name" type="category" width={100} className="text-xs" />
                        <Tooltip
                          contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }}
                          formatter={(value: number) => [`${value} consultant${value > 1 ? "s" : ""}`, ""]}
                        />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                          {ageData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                    {ageData.map((item) => {
                      const percentage = totalAppointments > 0 ? ((item.value / totalAppointments) * 100).toFixed(0) : "0";
                      return (
                        <div key={item.key} className="p-2 rounded-lg border bg-card text-center">
                          <div className="text-base sm:text-lg font-bold">{item.value}</div>
                          <div className="text-[9px] sm:text-[10px] text-muted-foreground leading-tight">{item.name}</div>
                          <div className="text-[9px] sm:text-[10px] text-muted-foreground">({percentage}%)</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Suggestions Tab */}
        <TabsContent value="suggestions">
          <AISuggestionsPanel statistics={statisticsForAI} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
